import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown, User, ScrollText, FileWarning, Vote, Landmark, Library, HandHeart } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { api, fileUrl } from '../lib/api';
import { SectionHeader, Rich, Loading, RichSection } from '../components/Shared';
import { MoreDetails } from '../components/MoreDetails';
import { useSectionOverride } from '../lib/pageContent';
import { FlyerDownloadSection } from '../components/FlyerDownload';
import { CvDownloadSection } from '../components/CvDownload';
import { StoryTimeline, ExperienceContent, WHY_REASONS } from './MeetSteve';

// Quick site guide: answers the seven first-visit questions in one glance.
const SITE_GUIDE = [
  { icon: User, q: 'Who is Steve?', a: 'You are in the right place. Keep reading below.', label: 'Read About Steve', url: '#why-im-running', anchor: true },
  { icon: ScrollText, q: 'What does he stand for?', a: 'Practical commitments for Ward 8.', label: 'My Platform', url: '/platform' },
  { icon: FileWarning, q: 'I have a City problem. What do I do?', a: 'Report it with PITA and get a tracking number.', label: 'Report an Issue', url: '/issue-tracker/report' },
  { icon: Vote, q: 'What about the election?', a: 'Dates, voting info, and who is running.', label: 'Election Centre', url: '/elections' },
  { icon: Landmark, q: 'What is happening in Ward 8?', a: 'Local issues, the map, and community updates.', label: 'Ward 8', url: '/ward-8' },
  { icon: Library, q: 'Where are the facts and reports?', a: 'City data, meetings, reports, and news.', label: 'Resources', url: '/resources' },
  { icon: HandHeart, q: 'How can I help?', a: 'Volunteer, share, or support the tools.', label: 'Get Involved', url: '/get-involved' },
];

const JUMP_LINKS = [
  { label: "Why I'm Running", hash: '#why-im-running' },
  { label: "Steve's Bio", hash: '#bio' },
  { label: "Steve's Story", hash: '#story' },
  { label: 'Experience', hash: '#experience' },
];

export default function StartHere() {
  const [page, setPage] = useState(null);
  const [whyPage, setWhyPage] = useState(null);
  const [expPage, setExpPage] = useState(null);
  const [stories, setStories] = useState([]);
  const [publicMedia, setPublicMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const heroOverride = useSectionOverride('start-here.header');

  useEffect(() => {
    Promise.allSettled([
      api.get('/pages/meet-steve'), api.get('/pages/why-im-running'), api.get('/pages/experience'),
      api.get('/stories'), api.get('/story-media/public'),
    ]).then(([p, w, e, s, m]) => {
      setPage(p.value?.data || null);
      setWhyPage(w.value?.data || null);
      setExpPage(e.value?.data || null);
      setStories(s.value?.data || []);
      setPublicMedia(Array.isArray(m.value?.data) ? m.value.data : []);
      setLoading(false);
    });
  }, []);

  if (loading) return <Loading />;

  const bannerImage = page?.hero_image || '/api/uploads/steve-fullbody-banner.webp';
  const heroKicker = heroOverride ? heroOverride.kicker : 'START HERE';
  const heroTitle = (heroOverride && heroOverride.title) || 'Meet Steve Lesperance';
  const heroIntro = heroOverride ? (heroOverride.intro || page?.subtitle) : page?.subtitle;

  const publishedSections = (page?.sections || []).filter((s) => s.published !== false);
  const whyTabSection = publishedSections.find((s) => /why/i.test(s.title || ''));
  const bioSections = publishedSections.filter((s) => s !== whyTabSection);
  const whyPageSections = (whyPage?.sections || []).filter((s) => s.published !== false);
  const mediaByStory = publicMedia.reduce((acc, m) => {
    if (m.attached_story_id) { (acc[m.attached_story_id] = acc[m.attached_story_id] || []).push(m); }
    return acc;
  }, {});

  return (
    <div>
      {/* Full-width hero banner (Steve full-body on white, never cropped) */}
      <div className="bg-white border-b border-slate-200" data-testid="start-here-hero">
        <div className="md:hidden px-4 pt-8 pb-2 max-w-6xl mx-auto">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#8a6f22] mb-2">{heroKicker}</p>
          <h1 className="text-3xl font-black text-[#1a2744] tracking-tight leading-tight">{heroTitle}</h1>
          {heroIntro && <p className="mt-3 text-base text-slate-600 leading-relaxed">{heroIntro}</p>}
          <p className="mt-3 text-sm font-bold text-[#8a6f22]">Lesperance is the French word for Hope</p>
        </div>
        <div className="relative" data-testid="start-here-hero-image">
          <img src={fileUrl(bannerImage)} alt="Steve Lesperance, Ward 8 Councillor candidate, full portrait" className="w-full h-auto block" />
          <div className="hidden md:flex absolute inset-y-0 left-0 w-[52%] items-center">
            <div className="pl-8 lg:pl-16 xl:pl-24 pr-6 max-w-2xl">
              <p className="text-xs lg:text-sm font-semibold uppercase tracking-widest text-[#8a6f22] mb-3">{heroKicker}</p>
              <h1 className="text-3xl lg:text-5xl xl:text-6xl font-black text-[#1a2744] tracking-tight leading-tight">{heroTitle}</h1>
              {heroIntro && <p className="mt-4 text-base lg:text-lg text-slate-600 leading-relaxed">{heroIntro}</p>}
              <p className="mt-4 text-sm lg:text-base font-bold text-[#8a6f22]">Lesperance is the French word for Hope</p>
            </div>
          </div>
        </div>
        {/* Section jump bar */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap gap-2" data-testid="start-here-jump-bar">
          {JUMP_LINKS.map((j) => (
            <a key={j.hash} href={j.hash} className="inline-flex items-center gap-1.5 min-h-[44px] px-4 py-2 rounded-full border border-[#1a2744]/25 bg-white text-sm font-bold text-[#1a2744] hover:border-[#24406f] hover:bg-[#eaf2ff] transition-colors duration-150" data-testid={`start-here-jump-${j.hash.slice(1)}`}>
              {j.label} <ArrowDown className="h-3.5 w-3.5 text-[#24406f]" />
            </a>
          ))}
        </div>
      </div>

      {/* Fast answers: the whole website in seconds */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="start-here-site-guide">
        <SectionHeader kicker="NEW HERE?" title="Find what you need in seconds" dek="Seven quick questions, seven clear answers." />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SITE_GUIDE.map(({ icon: Icon, q, a, label, url, anchor }) => (
            <Card key={q} className="card-lift" data-testid="start-here-guide-card">
              <CardContent className="p-5">
                <div className="h-10 w-10 rounded-xl bg-[#1a2744] flex items-center justify-center mb-3">
                  <Icon className="h-5 w-5 text-[#C9A84C]" />
                </div>
                <p className="font-serif font-bold text-[#1a2744] leading-snug">{q}</p>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{a}</p>
                {anchor ? (
                  <a href={url} className="inline-flex items-center gap-1 mt-3 text-sm font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4">{label} <ArrowDown className="h-3.5 w-3.5" /></a>
                ) : (
                  <Link to={url} className="inline-flex items-center gap-1 mt-3 text-sm font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4">{label} <ArrowRight className="h-3.5 w-3.5" /></Link>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* WHY I'M RUNNING */}
        <section id="why-im-running" className="pt-4" data-testid="start-here-why-section">
          <SectionHeader sectionKey="meet-steve.why" kicker="WHY I'M RUNNING" title="Real change, working systems, and answers for Ward 8" />
          <RichSection id="start-here-sections.why_intro" className="mb-5" />
          {whyTabSection && (
            <Card className="mb-6"><CardContent className="p-5 sm:p-8"><Rich html={whyTabSection.body_html} /></CardContent></Card>
          )}
          {whyPageSections.map((section) => (
            <div key={section.id} className="mb-6" data-testid="start-here-why-cms-section">
              {section.title && <h3 className="font-serif font-bold text-lg text-[#1a2744] mb-3">{section.title}</h3>}
              <Card><CardContent className="p-5 sm:p-8"><Rich html={section.body_html} /></CardContent></Card>
            </div>
          ))}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="start-here-why-cards">
            {WHY_REASONS.map((r) => (
              <Card key={r.title} className="card-lift" data-testid="start-here-why-card">
                <CardContent className="p-5">
                  <p className="font-serif font-bold text-[#1a2744]">{r.title}</p>
                  <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{r.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 bg-[#1a2744] rounded-2xl p-6 sm:p-10 text-center" data-testid="start-here-slogan-band">
            <p className="font-serif font-black text-white text-xl sm:text-2xl leading-snug">"I'm helping the people by building the tools needed to get the job done."</p>
            <p className="mt-3 text-slate-300">Steve promises accountability by building the system for it.</p>
            <p className="mt-4 text-sm font-black uppercase tracking-widest text-[#C9A84C]">Truth Before Spin. Answers Before Excuses. Service Before Politics.</p>
            <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
              <Button asChild className="h-12 bg-white hover:bg-slate-100 text-[#1a2744] font-bold btn-press" data-testid="start-here-pita-button"><Link to="/issue-tracker">Ward 8 Issue Tracker App (PITA) <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
              <Button asChild variant="outline" className="h-12 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press"><Link to="/platform">See Steve's Platform</Link></Button>
            </div>
          </div>
        </section>

        {/* BIO: simple first, full biography behind More... */}
        <section id="bio" className="pt-12" data-testid="start-here-bio-section">
          <SectionHeader kicker="BIOGRAPHY" title="The full biography" dek="The short story is above. Open this for the complete background and the reasons Ward 8 residents are backing Steve." />
          <MoreDetails testId="start-here-bio-more">
          {bioSections.map((section) => (
            <div key={section.id} className="mb-8">
              <SectionHeader kicker={section.kicker} title={section.title} />
              <Card><CardContent className="p-5 sm:p-8"><Rich html={section.body_html} /></CardContent></Card>
            </div>
          ))}
          <SectionHeader kicker="WHY VOTE STEVE?" title="Eight reasons Ward 8 residents are backing Steve" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(page?.why_vote_cards || []).map((card) => (
              <Card key={card.id} className="card-lift" data-testid="start-here-vote-card">
                <CardContent className="p-5">
                  <p className="font-serif font-bold text-[#1a2744]">{card.title}</p>
                  <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{card.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          </MoreDetails>
        </section>

        {/* STORY */}
        <section id="story" className="pt-12" data-testid="start-here-story-section">
          <SectionHeader kicker="STEVE'S STORY" title="A life of communication, systems, and standing up" dek="Concerns described below are raised in the public interest. Supporting documents and source material are uploaded where available. Open More for the full project history." />
          {stories.length === 0 ? <p className="text-slate-500">Story blocks are being prepared.</p> : (
            <MoreDetails testId="start-here-story-more">
              <StoryTimeline stories={stories} mediaByStory={mediaByStory} />
            </MoreDetails>
          )}
        </section>

        {/* EXPERIENCE */}
        <section id="experience" className="pt-12" data-testid="start-here-experience-section">
          <SectionHeader kicker="EXPERIENCE" title="Steve's experience" dek="Decades of hands-on work. Open More for the full record." />
          <MoreDetails testId="start-here-experience-more">
            <ExperienceContent expPage={expPage} publicMedia={publicMedia} />
          </MoreDetails>
        </section>

        {/* Downloads + next steps */}
        <div className="mt-12 space-y-4">
          <CvDownloadSection loc="meet-steve" />
          <FlyerDownloadSection loc="meet-steve" />
        </div>
        <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5" data-testid="start-here-next-steps">
          <div className="flex-1">
            <p className="font-serif font-black text-[#1a2744] text-xl">You know Steve now. Here is the next step.</p>
            <p className="text-sm text-slate-600 mt-1">See exactly what he stands for and how you will be able to check it.</p>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-2 w-full sm:w-auto">
            <Button asChild className="h-13 min-h-[52px] px-8 bg-[#1a2744] hover:bg-[#24406f] text-white text-base font-black btn-press w-full sm:w-auto" data-testid="start-here-next-platform"><Link to="/platform">See My Platform <ArrowRight className="h-5 w-5 ml-1.5" /></Link></Button>
            <Link to="/issue-tracker/report" className="text-sm font-bold text-[#b91c1c] underline decoration-[#b91c1c]/40 decoration-2 underline-offset-4 hover:decoration-[#b91c1c]" data-testid="start-here-next-report">Or report a City issue now</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
